// Audible alerts for admin action events.
//
// Two parts: a short WebAudio chime to grab attention, then a SPOKEN phrase so
// the operator knows WHAT arrived without looking ("You have a new order" /
// "You have a new user binding request"). Speech uses the browser's built-in
// speechSynthesis — no audio files to ship or host.
//
// Browser autoplay policy: audio is blocked until the page has had a real user
// gesture, so an alert arriving on a freshly-loaded page is silent through no
// fault of ours. installAudioUnlock() resumes on the first click/key/touch, and
// isAudioLocked() lets the UI show a "click to enable sound" hint rather than
// leaving the operator wondering why it was quiet.

const MUTE_KEY = 'primus_alert_muted';
const DEBOUNCE_MS = 2000; // collapse a burst into a single announcement

let audioCtx = null;
let lastPlay = 0;
let gestureBound = false;
const muteListeners = new Set();

function getCtx() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) {
    try { audioCtx = new AC(); } catch { audioCtx = null; }
  }
  return audioCtx;
}

export function isMuted() {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export function setMuted(muted) {
  try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* ignore */ }
  if (!muted) primeAudio(); // un-muting is itself the unlocking gesture
  else stopSpeech();
  muteListeners.forEach((fn) => { try { fn(muted); } catch { /* ignore */ } });
}

export function subscribeMute(handler) {
  if (typeof handler !== 'function') return () => {};
  muteListeners.add(handler);
  handler(isMuted());
  return () => { muteListeners.delete(handler); };
}

export function primeAudio() {
  const ctx = getCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
}

// True when the browser is still blocking audio (no user gesture yet). The UI
// uses this to prompt for a click instead of silently failing.
export function isAudioLocked() {
  if (typeof window === 'undefined') return false;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return false;
  // Don't CREATE a context just to test — only report on one we already made.
  return !!audioCtx && audioCtx.state === 'suspended';
}

function playChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const t0 = ctx.currentTime;
  const notes = [
    { freq: 659.25, start: 0.0, dur: 0.12 }, // E5
    { freq: 880.0, start: 0.1, dur: 0.18 },  // A5
  ];
  for (const n of notes) {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = n.freq;
      const s = t0 + n.start;
      gain.gain.setValueAtTime(0.0001, s);
      gain.gain.exponentialRampToValueAtTime(0.22, s + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, s + n.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(s);
      osc.stop(s + n.dur + 0.02);
    } catch { /* never break the caller */ }
  }
}

export function stopSpeech() {
  try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* ignore */ }
}

function speak(text) {
  if (!text) return;
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel(); // never let announcements queue up into a backlog
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = 1.0;
    synth.speak(u);
  } catch { /* speech unsupported — the chime still fired */ }
}

// Chime, then say what arrived. `message` e.g. "You have a new order".
export function announce(message) {
  if (isMuted()) return;
  const now = Date.now();
  if (now - lastPlay < DEBOUNCE_MS) return; // one announcement per burst
  lastPlay = now;
  playChime();
  // Let the chime land first so they don't talk over each other.
  setTimeout(() => { if (!isMuted()) speak(message); }, 320);
}

// Install one-shot gesture listeners so audio unlocks as soon as the operator
// interacts with the page. Idempotent; safe to call on every mount.
export function installAudioUnlock() {
  if (gestureBound || typeof window === 'undefined') return;
  gestureBound = true;
  const unlock = () => primeAudio();
  ['click', 'keydown', 'touchstart'].forEach((ev) =>
    window.addEventListener(ev, unlock, { once: true, passive: true })
  );
  // Create the context up front so isAudioLocked() can report honestly.
  getCtx();
}
